// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ProfitCalculator } from './ProfitCalculator';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { Resources } from '@shared/localizations/Resources';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { AccountType } from '@shared/utils/Account/AccountType';
import { QuoteValid } from '@shared/utils/Quotes/QuoteValid';
import { Order } from './Order';
import { OrderEditRequestData } from '@shared/utils/Trading/OrderEditRequestData';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { SlTpHolder } from '@shared/utils/Trading/SlTpHolder';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { SlTpPriceType } from '@shared/utils/Enums/Constants';
export class Position extends Order {
    get isAllowOTHTradingSL() {
        if (!isNullOrUndefined(this.SLOrder)) {
            return this.SLOrder.IsAllowOTHTrading;
        }
        else {
            return null;
        }
    }
    get isAllowOTHTradingTP() {
        if (!isNullOrUndefined(this.TPOrder)) {
            return this.TPOrder.IsAllowOTHTrading;
        }
        else {
            return null;
        }
    }
    constructor(dataCache, quoteController, paramObj) {
        super(dataCache, quoteController, {
            OrderNumber: paramObj.PositionId,
            OrderType: 0,
            Amount: paramObj.Amount,
            Price: paramObj.OpenPrice,
            BuySell: paramObj.OperationType,
            TimeInForce: 0,
            UTCDateTime: paramObj.OpenTime,
            Active: true,
            Route: paramObj.Route,
            Account: paramObj.Account,
            Instrument: paramObj.Instrument,
            ProductType: paramObj.ProductType,
            Leverage: paramObj.Leverage,
            ServerCalculation: paramObj.ServerCalculation
        });
        this.OpenCrossPrice = 1;
        this.AccruedInterest = null;
        // #80010
        // TODO. Refactor.
        this.AvailableForSell = 0;
        this.Commission = 0;
        this.Swaps = 0;
        this.OptionExerciseStatus = null;
        this.CorporateActionType = null;
        this.StartOfDayAmount = null;
        this.SLOrder = null;
        this.TPOrder = null;
        this.PositionId = paramObj.PositionId;
        this.SuperPositionId = paramObj.SuperPositionId;
        this.OpenOrderId = paramObj.OpenOrderId;
        this.RealizedPL = paramObj.RealizedPL;
        this.OpenTime = paramObj.OpenTime;
        this.OpenPrice = paramObj.OpenPrice;
        this.AccruedInterest = paramObj.AccruedInterest;
        this.isPosition = true;
        this.isSuperPosition = this.PositionId == this.SuperPositionId; // is super position of FIFO Position #80010
        this.isTradePosition = this.SuperPositionId != '-1' && !this.isSuperPosition; // is sub position of FIFO Position
        this.UnitVolume = this.Amount * this.Instrument.getLotSize();
        this.amountExp1 = this.BuySell === OperationType.Buy ? this.UnitVolume : -this.UnitVolume;
        this.PnLCalculator = new ProfitCalculator(this);
    }
    get AmountAvailableForSell() {
        return this.Amount - (this.AvailableForSell || 0);
    }
    // #93540
    get IsActivated() {
        var _a;
        return (_a = this.SLOrder) === null || _a === void 0 ? void 0 : _a.IsActivated;
    }
    GetBasePriceForTrailingStop() {
        if (this.lastPriceUpdated) {
            return this.CurPriceClose;
        }
        else {
            return this.Price;
        }
    }
    createSlTpHolder() {
        const sltpholder = new SlTpHolder();
        if (!isNullOrUndefined(this.SLOrder)) {
            switch (this.SLOrder.OrderType) {
                case OrderType.TrailingStop:
                    sltpholder.StopLossPriceType = SlTpPriceType.TrOffset;
                    sltpholder.StopLossPriceValue = this.SLOrder.TrStopOffset;
                    break;
                case OrderType.StopLimit:
                    sltpholder.StopLossPriceValue = this.SLOrder.StopLimit;
                    sltpholder.StopLossLimitPriceValue = this.SLOrder.Price;
                    break;
                default:
                    sltpholder.StopLossPriceValue = this.SLOrder.Price;
                    break;
            }
        }
        if (!isNullOrUndefined(this.TPOrder)) {
            sltpholder.TakeProfitPriceValue = this.TPOrder.Price;
        }
        return sltpholder;
    }
    // TODO.
    UpdateByMessage(message) {
        this.Route = message.Route || this.Route;
        this.Amount = message.Amount || this.Amount;
        this.Price = message.OpenPrice || this.Price;
        this.OpenPrice = message.OpenPrice || this.OpenPrice;
        this.OpenTime = message.OpenTime || this.OpenTime;
        this.OpenOrderId = message.OpenOrderId || this.OpenOrderId;
        this.RealizedPL = message.RealizedPL || this.RealizedPL;
        this.ProductType = message.ProductType || this.ProductType;
        this.SuperPositionId = message.SuperPositionId || this.SuperPositionId;
        if (message.hasOwnProperty('Swaps')) {
            this.Swaps = message.Swaps;
        }
        if (message.hasOwnProperty('Commission')) {
            this.Commission = message.Commission;
        }
        if (message.hasOwnProperty('OptionExerciseStatus')) {
            this.OptionExerciseStatus = message.OptionExerciseStatus;
            if (this.IsPendingExerciseOptionStatus()) // 93127: При смене статуса для позиций на Pending exercise. Нужно выводить поверх всех окон скрин
             {
                this.DataCache.PositionExerciseStatusChanged(this.PositionId);
            }
        }
        if (message.CorporateActionType) {
            this.CorporateActionType = message.CorporateActionType;
        }
        const serverCalculation = this.ServerCalculation;
        if (message.ServerCalculationCurPriceClose) {
            serverCalculation.CurPriceClose = message.ServerCalculationCurPriceClose;
        }
        if (message.ServerCalculationExposure) {
            serverCalculation.Exposure = message.ServerCalculationExposure;
        }
        if (message.ServerCalculationPositionValue) {
            serverCalculation.PositionValue = message.ServerCalculationPositionValue;
        }
        if (message.ServerCalculationNetPL) {
            serverCalculation.NetPL = message.ServerCalculationNetPL;
        }
        if (message.ServerCalculationGrossPL) {
            serverCalculation.GrossPL = message.ServerCalculationGrossPL;
        }
        // Taken from Order.newQuote() ServerCalculation exists only in positions;
        if (serverCalculation != null && serverCalculation.CurPriceClose !== null) {
            this.CurPriceClose = this.CurPriceCloseNotRounded = serverCalculation.CurPriceClose;
        }
        this.UnitVolume = this.Amount * this.Instrument.getLotSize();
        this.amountExp1 = this.BuySell === OperationType.Buy ? this.UnitVolume : -this.UnitVolume;
        this.StartOfDayAmount = message.StartOfDayAmount;
        if (message.Leverage) {
            this.Leverage = message.Leverage;
        }
        if (message.StrategyId !== null) {
            this.StrategyId = message.StrategyId;
        }
        if (message.AccruedInterest != null) {
            this.AccruedInterest = message.AccruedInterest;
        }
        this.OnUpdate.Raise(this);
    }
    newQuote(mess) {
        // Order.prototype.newQuote.call(this, mess);
        let quote = null;
        if (mess.Type === HistoryType.QUOTE_INSTRUMENT_DAY_BAR || mess.Type === HistoryType.QUOTE_TRADES) {
            quote = this.Instrument.GetLastQuote(QuoteValid.Last);
        }
        if (mess.Type === HistoryType.QUOTE_LEVEL1 || quote != null) {
            const quote1Msg = mess.Type === HistoryType.QUOTE_LEVEL1 ? mess : quote;
            const lastPrice = this.Instrument.Level1.GetLastPrice(this.Account);
            if (!this.Instrument.CalcPriceOnTrade() && quote1Msg != null) {
                const sp = this.DataCache.GetSpreadPlan(this.Account);
                // по л1 всегда и точно всегда (и никаого тут трейда/ласта!!!)
                if (this.BuySell === OperationType.Buy) {
                    if (isNaN(quote1Msg.Bid)) {
                        this.CurPriceClose = this.CurPriceCloseNotRounded = lastPrice;
                    }
                    else {
                        this.CurPriceClose = quote1Msg.BidSpread_SP_Ins(sp, this.Instrument);
                        this.CurPriceCloseNotRounded = quote1Msg.BidSpread_SP_Ins(sp, this.Instrument, false);
                    }
                }
                else {
                    if (isNaN(quote1Msg.Ask)) {
                        this.CurPriceClose = this.CurPriceCloseNotRounded = lastPrice;
                    }
                    else {
                        this.CurPriceClose = quote1Msg.AskSpread_SP_Ins(sp, this.Instrument);
                        this.CurPriceCloseNotRounded = quote1Msg.AskSpread_SP_Ins(sp, this.Instrument, false);
                    }
                }
                this.lastPriceUpdated = true;
                if (isNaN(this.CurPriceClose)) {
                    this.CurPriceClose = this.CurPriceCloseNotRounded = lastPrice;
                }
            }
            else {
                // беру ласт (если он есть)
                // double last = quote1Msg.LastPice.GetValueOrDefault(); //quote1Msg.LastSpread(FAccount, instrument); //ласт не спредировать, брать оригинальный
                if (!isNaN(lastPrice)) {
                    this.CurPriceClose = this.CurPriceCloseNotRounded = lastPrice;
                    this.lastPriceUpdated = true;
                }
                // тут тоже можно превклоуз взять, когда нет трейда
            }
            this.LastQuote = quote1Msg;
            if (this.SLOrder) {
                this.SLOrder.newQuote(mess);
            }
            if (this.TPOrder) {
                this.TPOrder.newQuote(mess);
            }
            this.OnUpdate.Raise(this);
        }
        if (this.Instrument !== null &&
            (this.Instrument.InstrumentDayBarMessageUpdateMode || mess.Type == HistoryType.QUOTE_INSTRUMENT_DAY_BAR)) {
            if (this.Instrument.CalcPriceOnTrade()) {
                const last = this.Instrument.Level1.GetLastPrice(this.Account);
                if (!isNaN(last) && last !== this.CurPriceClose) {
                    this.CurPriceClose = this.CurPriceCloseNotRounded = last;
                    this.lastPriceUpdated = true;
                }
            }
        }
        else if (mess.Type === HistoryType.QUOTE_TRADES) {
            if (this.Instrument.CalcPriceOnTrade()) {
                this.CurPriceClose = this.CurPriceCloseNotRounded = mess.Price;
                this.lastPriceUpdated = true;
            }
        }
    }
    RecalcAmounts() {
        const curPriceAvailable = !isNaN(this.CurPriceClose);
        const buySell = this.BuySell;
        const openPrice = this.OpenPrice;
        const crossInstrumentUSD = this.DataCache.CrossRateCache.GetCrossPriceIns(this.Account, this.Instrument, buySell);
        let openCrossPrice = this.OpenCrossPrice;
        const ticCost = this.Instrument.GetTickCost();
        let USCPFOC = true;
        if (this.Account.RiskPlan !== null) {
            const rps = this.Account.RiskPlan.riskPlanCache.getRiskPlanSettings(this.Instrument, this.ProductType, 'UseSameCrossPriceForOpenClose');
            USCPFOC = rps.getValue(this.ProductType, 'UseSameCrossPriceForOpenClose');
        }
        if (USCPFOC || openCrossPrice === 0) {
            let crossInstrumentAcc = this.DataCache.CrossRateCache.GetCrossPriceInsSideExp2(this.Account, this.Instrument, buySell);
            if (this.Account.AccountType === AccountType.MultiAsset) {
                crossInstrumentAcc = 1.0;
            } // сейчас позиций по мульта аккам не бывает, и всё же.
            openCrossPrice = crossInstrumentAcc;
        }
        if (curPriceAvailable) {
            this.PnLCalculator.Calculate();
        }
        const faceValueMultiplier = this.Instrument.GetFaceValueMultiplier();
        this.expositionUSD = Math.abs(this.amountExp1) * openPrice * crossInstrumentUSD * ticCost * faceValueMultiplier;
        this.expositionAccountCurr =
            Math.abs(this.amountExp1) * openPrice * openCrossPrice * ticCost * faceValueMultiplier;
        if (curPriceAvailable) {
            this.positionValueUSD =
                this.expositionUSD + (this.BuySell === OperationType.Buy ? 1 : -1) * this.PnLCalculator.NetGrossPL; // #85612
            this.positionValueAcc =
                this.expositionAccountCurr +
                    (this.BuySell === OperationType.Buy ? 1 : -1) * this.PnLCalculator.AccGrossPL; // #85612
        }
    }
    getNetPnL(useAccountCurr) {
        return useAccountCurr ? this.PnLCalculator.AccNetPL : this.PnLCalculator.NetPL;
    }
    getGrossPnL(useAccountCurr) {
        return useAccountCurr ? this.PnLCalculator.AccGrossPL : this.PnLCalculator.NetGrossPL;
    }
    getMaintMargin(useAccountCurr) {
        return 0;
        // if (useAccountCurr || this.Account == null) { return maintMargin; } else { return maintMargin / this.Account.getCrossPrice(); }
    }
    GetCommissionFromSettlement(useAccountCurr) {
        if (useAccountCurr) {
            return this.Commission;
        }
        else {
            return this.Commission / this.Account.getCrossPriceCorrect();
        }
    }
    getPositionValue(useAccountCurr) {
        if (useAccountCurr) {
            return this.positionValueAcc;
        }
        else {
            return this.positionValueUSD;
        }
    }
    GetSwaps(useAccCurrency) {
        return this.Swaps;
    }
    getExpositionValue(useAccountCurr) {
        if (useAccountCurr) {
            return this.expositionAccountCurr;
        }
        else {
            return this.expositionUSD;
        }
    }
    GetMarkup() {
        var _a;
        let crossInstrumentAcc = this.DataCache.CrossRateCache.GetCrossPriceInsSideExp2(this.Account, this.Instrument, this.BuySell);
        if (this.Account != null && this.Account.AccountType === AccountType.MultiAsset) {
            crossInstrumentAcc = 1.0;
        } // сейчас позиций по мульта аккам не бывает, и всё же.
        let markupInstrumentAcc = null;
        if (((_a = this.Account) === null || _a === void 0 ? void 0 : _a.CrossratesPlan) != null) {
            markupInstrumentAcc = this.Account.CrossratesPlan.GetMarkup(this.Instrument.Exp2, this.Account.BaseCurrency, crossInstrumentAcc, this.Instrument, this.BuySell === OperationType.Buy);
        }
        if (markupInstrumentAcc) {
            return markupInstrumentAcc.markup;
        }
        return 0;
    }
    IsPendingExerciseOptionStatus() {
        return this.OptionExerciseStatus === Position.OPTION_EXERCISE_STATUS.PENDING_EXERCISE; // #93127
    }
    GetInstrumentDisplayName(forceHideRoute = false) {
        const displayName = this.Instrument.DisplayName();
        if (this.CorporateActionType !== null && CorporateActionTypeMap[this.CorporateActionType]) {
            const corporateActionText = Resources.getResource('corporateActionType.' + CorporateActionTypeMap[this.CorporateActionType]);
            if (this.Instrument.isHideRouteMode || forceHideRoute || this.Instrument.FLanguageAliases) {
                return displayName + ' (' + corporateActionText + ')';
            }
            else {
                return this.Instrument.ShortName + ' (' + corporateActionText + ')' + ':' + this.Instrument.RouteName;
            }
        }
        else {
            return displayName;
        }
    }
    GetInstrumentDescriptionValue() {
        const descriptionValue = this.Instrument.DescriptionValue();
        if (this.CorporateActionType !== null && CorporateActionTypeMap[this.CorporateActionType]) {
            const corporateActionText = Resources.getResource('corporateActionType.' + CorporateActionTypeMap[this.CorporateActionType]);
            return descriptionValue + ' (' + corporateActionText + ')';
        }
        else {
            return descriptionValue;
        }
    }
    GetOrderEditRequestData() {
        const data = new OrderEditRequestData();
        Object.assign(data, {
            position: this,
            instrument: this.Instrument,
            account: this.Account,
            quantity: new Quantity(this.Amount, true),
            side: this.BuySell,
            productType: this.ProductType,
            leverageValue: this.Leverage
        });
        return data;
    }
}
// тут ли им место? или в OrderExecutor? чи куды???
Position.OPTION_EXERCISE_STATUS = {
    PENDING_EXERCISE: 0,
    EXERCISED: 1,
    CANCELLED: 2,
    BLANK: -1
};
// #region ServerCalculation
export class ServerCalculation {
    constructor(curPriceClose, exposure, positionValue, netPL, grossPL) {
        this.Exposure = exposure;
        this.PositionValue = positionValue;
        this.NetPL = netPL;
        this.GrossPL = grossPL;
        this.CurPriceClose = curPriceClose;
    }
}
// #endregion ServerCalculation
export var CorporateActionType;
(function (CorporateActionType) {
    CorporateActionType[CorporateActionType["BonusIssue"] = 1] = "BonusIssue";
    CorporateActionType[CorporateActionType["CashDividends"] = 2] = "CashDividends";
    CorporateActionType[CorporateActionType["StockDividends"] = 3] = "StockDividends";
    CorporateActionType[CorporateActionType["SpinOff"] = 5] = "SpinOff";
    CorporateActionType[CorporateActionType["RightsIssue"] = 6] = "RightsIssue";
})(CorporateActionType || (CorporateActionType = {}));
export const CorporateActionTypeMap = {
    1: 'BonusIssue',
    2: 'CashDividends',
    3: 'StockDividends',
    5: 'SpinOff',
    6: 'RightsIssue'
};
